/**
 * Naming a playlist and deciding who it belongs to.
 *
 * Scope is settable once, when the playlist is created, and read-only afterwards.
 * The store partition is part of a playlist's identity in the table, so "move
 * this to another store" is not an edit — it is a new playlist and a stale
 * reference on every screen that pointed at the old one. Offering it as a
 * dropdown that silently does something else would be worse than not offering it.
 */

import { useMemo, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { errorMessage } from '../../lib/api.js';
import type { StoreRecord } from '../../lib/schemas.js';

export interface PlaylistFormValue {
  name: string;
  storeId: string | null;
  brands: string[];
}

export interface PlaylistFormProps {
  mode: 'create' | 'rename';
  initialName: string;
  initialStoreId: string | null;
  initialBrands: readonly string[];
  /** The stores this user may write a playlist into. */
  stores: readonly StoreRecord[];
  canWriteShared: boolean;
  busy: boolean;
  error: unknown;
  onSubmit: (value: PlaylistFormValue) => void;
  onCancel: () => void;
}

const SHARED = '__shared__';

export function PlaylistForm({
  mode,
  initialName,
  initialStoreId,
  initialBrands,
  stores,
  canWriteShared,
  busy,
  error,
  onSubmit,
  onCancel,
}: PlaylistFormProps): ReactElement {
  const [name, setName] = useState(initialName);
  const [scope, setScope] = useState<string>(initialStoreId ?? (canWriteShared ? SHARED : ''));
  const [brands, setBrands] = useState<readonly string[]>(initialBrands);
  const [touched, setTouched] = useState(false);

  const brandOptions = useMemo(
    () => [...new Set(stores.map((store) => store.brand))].sort((a, b) => a.localeCompare(b)),
    [stores],
  );

  const trimmed = name.trim();
  const nameProblem = trimmed === '' ? 'A playlist needs a name you will recognise in a list.' : null;
  const scopeProblem = scope === '' ? 'Choose where this playlist lives.' : null;
  const blocked = nameProblem !== null || scopeProblem !== null;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setTouched(true);
    if (blocked || busy) return;
    onSubmit({
      name: trimmed,
      storeId: scope === SHARED ? null : scope,
      brands: scope === SHARED ? [...brands] : [],
    });
  };

  return (
    <form className="form" onSubmit={submit} noValidate>
      <div className="form__grid">
        <label className="field field--span">
          <span className="field__label">Name</span>
          <input
            className="input"
            value={name}
            maxLength={120}
            autoFocus
            onChange={(event) => setName(event.currentTarget.value)}
            onBlur={() => setTouched(true)}
          />
          {touched && nameProblem !== null ? (
            <span className="field__error">{nameProblem}</span>
          ) : (
            <span className="field__hint">
              Managers pick playlists from a list of names. &ldquo;Lunch loop&rdquo; beats
              &ldquo;Playlist 2&rdquo;.
            </span>
          )}
        </label>

        <label className="field field--span">
          <span className="field__label">Belongs to</span>
          <select
            className="input"
            value={scope}
            disabled={mode === 'rename'}
            onChange={(event) => setScope(event.currentTarget.value)}
          >
            <option value="">Choose…</option>
            {canWriteShared ? <option value={SHARED}>Shared across every store</option> : null}
            {stores.map((store) => (
              <option key={store.storeId} value={store.storeId}>
                {store.name}
              </option>
            ))}
          </select>
          {touched && scopeProblem !== null ? (
            <span className="field__error">{scopeProblem}</span>
          ) : (
            <span className="field__hint">
              {mode === 'rename'
                ? 'Fixed once created: screens point at a playlist by id inside its store, so moving one would leave those references behind.'
                : 'A store playlist can use that store’s media and anything shared. A shared playlist can only use shared media.'}
            </span>
          )}
        </label>

        {scope === SHARED && brandOptions.length > 0 ? (
          <fieldset className="field field--span">
            <legend className="field__label">Brands that can see it</legend>
            <div className="brand-choices">
              {brandOptions.map((brand) => (
                <label key={brand} className="checkbox">
                  <input
                    type="checkbox"
                    checked={brands.includes(brand)}
                    onChange={(event) =>
                      setBrands((current) =>
                        event.currentTarget.checked
                          ? [...current, brand]
                          : current.filter((entry) => entry !== brand),
                      )
                    }
                  />
                  <span>{brand}</span>
                </label>
              ))}
            </div>
            <span className="field__hint">
              Leave every box clear for every brand. Ticking some limits it to those — a Rosetown
              Kebab promo has no business on a Pizza Bros wall.
            </span>
          </fieldset>
        ) : null}
      </div>

      {error !== null && error !== undefined ? (
        <p className="alert alert--danger" role="alert">
          {errorMessage(error)}
        </p>
      ) : null}

      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Saving…' : mode === 'create' ? 'Create playlist' : 'Save name'}
        </button>
      </div>
    </form>
  );
}
